import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaLock, FaKey, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import { GiFairyWings } from 'react-icons/gi';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './ChangePassword.css';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // --- Real-time Validation Checks ---
  const hasLength = newPassword.length >= 6;
  const hasLower = /[a-z]/.test(newPassword);
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasDigit = /\d/.test(newPassword);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setIsError(false);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage('All fields are required.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!hasLength || !hasLower || !hasUpper || !hasDigit || !hasSpecial) {
      setMessage('Please ensure all password requirements are met.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setMessage('New passwords do not match.');
      setIsError(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/api/admin/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      setMessage(response.data.message);
      setIsError(false);
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);

    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Something went wrong';
      setMessage(errorMsg);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Helmet>
                <title>Change Password | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="Change your password for the Rajput Shiksha Kosh admin panel."
                />
              </Helmet>
    <div className="change-password-page-wrapper">
      <div className="change-password-page-card">

        {/* --- Header & Divider --- */}
        <div className="change-password-page-header">
            <h2 className="change-password-page-title">CHANGE PASSWORD</h2>
            <div className="change-password-page-divider">
              <span className="change-password-page-line"></span>
              <GiFairyWings className="change-password-page-icon-wings" />
              <span className="change-password-page-line"></span>
            </div>
            <p className="change-password-page-subtitle">Secure your account with a strong password.</p>
        </div>

        {/* Feedback Message */}
        {message && (
          <div className={`change-password-page-message ${isError ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* --- FORM --- */}
        <form onSubmit={handleSubmit}>
            {/* Current Password */}
            <div className="change-password-page-input-wrapper">
                <FaKey className="change-password-page-icon" />
                <input
                    className="change-password-page-input"
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current Password"
                    required
                />
                <button 
                    type="button"
                    className="change-password-page-eye-btn"
                    onClick={() => setShowCurrent(!showCurrent)}
                >
                    {showCurrent ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* New Password */}
            <div className="change-password-page-input-wrapper">
                <FaLock className="change-password-page-icon" />
                <input
                    className="change-password-page-input"
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New Password"
                    required
                />
                <button 
                    type="button"
                    className="change-password-page-eye-btn"
                    onClick={() => setShowNew(!showNew)}
                >
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* Confirm Password */}
            <div className="change-password-page-input-wrapper">
                <FaLock className="change-password-page-icon" />
                <input
                    className="change-password-page-input"
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm New Password"
                    required
                />
                <button 
                    type="button"
                    className="change-password-page-eye-btn"
                    onClick={() => setShowConfirm(!showConfirm)}
                >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>

            {/* --- Dynamic Password Requirements (Grid Layout) --- */}
            <div className="change-password-page-requirements">
                <span className={hasLength ? "req-met" : "req-unmet"}>
                    {hasLength ? <FaCheckCircle /> : <FaRegCircle />} Min 6 Chars
                </span>
                <span className={hasUpper ? "req-met" : "req-unmet"}>
                    {hasUpper ? <FaCheckCircle /> : <FaRegCircle />} 1 Uppercase
                </span>
                <span className={hasLower ? "req-met" : "req-unmet"}>
                    {hasLower ? <FaCheckCircle /> : <FaRegCircle />} 1 Lowercase
                </span>
                <span className={hasDigit ? "req-met" : "req-unmet"}>
                    {hasDigit ? <FaCheckCircle /> : <FaRegCircle />} 1 Number
                </span>
                <span className={hasSpecial ? "req-met" : "req-unmet"}>
                    {hasSpecial ? <FaCheckCircle /> : <FaRegCircle />} 1 Special Char
                </span>
                <span className={passwordsMatch ? "req-met" : "req-unmet"}>
                    {passwordsMatch ? <FaCheckCircle /> : <FaRegCircle />} Match Passwords
                </span>
            </div>

            {/* Submit Button (Donate Now Style) */}
            <button type="submit" className="change-password-page-submit-btn" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default ChangePassword;